import {DropboxClientContext} from 'App';
import {t} from 'i18next';
import {useCallback, useContext, useEffect, useRef, useState} from 'react';

import {useAppPublisher} from './AppPublisher';
import {filesFromDataTransfer} from './droppedFiles';
import {AppPublishResult, ExistingSitePublishTarget} from './publishApp';
import {UploadValidationIssue, UploadValidationResult, validateAppUpload} from './uploadValidation';

export const directoryInputProps = {
  directory: '',
  webkitdirectory: '',
} as React.InputHTMLAttributes<HTMLInputElement>;

export const formatUploadBytes = (bytes: number) => {
  if (bytes < 1024) {
    return t('apps_upload_size_bytes', {count: bytes});
  }
  if (bytes < 1024 * 1024) {
    return t('apps_upload_size_kb', {count: Math.ceil(bytes / 1024)});
  }
  return t('apps_upload_size_mb', {count: Math.ceil(bytes / (1024 * 1024))});
};

export const uploadIssueMessage = (issue: UploadValidationIssue) => {
  switch (issue.code) {
    case 'empty':
      return t('apps_upload_error_empty');
    case 'invalid_name':
      return t('apps_upload_error_invalid_name');
    case 'missing_index':
      return t('apps_upload_error_missing_index');
    case 'too_many_files':
      return t('apps_upload_error_too_many_files', {limit: issue.limit});
    case 'file_too_large':
      return t('apps_upload_error_file_too_large', {
        name: issue.name,
        limit: formatUploadBytes(issue.limitBytes),
      });
    case 'total_too_large':
      return t('apps_upload_error_total_too_large', {limit: formatUploadBytes(issue.limitBytes)});
    case 'unsafe_path':
      return t('apps_upload_error_unsafe_path', {name: issue.name});
    case 'duplicate_path':
      return t('apps_upload_error_duplicate_path', {name: issue.name});
    case 'reserved_path':
      return t('apps_upload_error_reserved_path', {name: issue.name});
    case 'unsupported_file':
      return t('apps_upload_error_unsupported_file', {name: issue.name});
  }
};

export const formatUploadValidationErrorLabel = (issues: UploadValidationIssue[]) =>
  `${t('apps_upload_validation_error_prefix')} ${issues.map(uploadIssueMessage).join(' ')}`;

export const useUploadDropzoneFlow = ({
  existingSite,
  onPublishSuccess,
}: {
  existingSite?: ExistingSitePublishTarget;
  onPublishSuccess: (result: AppPublishResult) => void;
}) => {
  const client = useContext(DropboxClientContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectionGenerationRef = useRef(0);
  const [validation, setValidation] = useState<UploadValidationResult | null>(null);
  const readyUpload = validation?.ok ? validation.upload : null;
  const issues = validation && !validation.ok ? validation.issues : [];
  const publisher = useAppPublisher(readyUpload, existingSite);
  const resetPublisher = publisher.reset;

  useEffect(() => {
    if (publisher.status === 'success' && publisher.result) {
      onPublishSuccess(publisher.result);
    }
  }, [onPublishSuccess, publisher.result, publisher.status]);

  const isPublishingRef = useRef(publisher.isPublishing);
  isPublishingRef.current = publisher.isPublishing;

  const stageResolvedFiles = useCallback(
    (files: File[] | FileList | null) => {
      if (!files?.length || isPublishingRef.current) {
        return;
      }
      resetPublisher();
      setValidation(validateAppUpload(files));
    },
    [resetPublisher]
  );

  const stageFiles = useCallback(
    (files: File[] | FileList | null) => {
      selectionGenerationRef.current += 1;
      stageResolvedFiles(files);
    },
    [stageResolvedFiles]
  );

  const stageDroppedFiles = useCallback(
    async (dataTransfer: DataTransfer) => {
      const generation = ++selectionGenerationRef.current;
      let files: File[] | FileList;
      try {
        files = await filesFromDataTransfer(dataTransfer);
      } catch {
        files = dataTransfer.files;
      }

      if (generation !== selectionGenerationRef.current || isPublishingRef.current) {
        return;
      }
      stageResolvedFiles(files);
    },
    [stageResolvedFiles]
  );

  const openPicker = useCallback(() => {
    if (!isPublishingRef.current) {
      inputRef.current?.click();
    }
  }, []);

  const reset = useCallback(() => {
    selectionGenerationRef.current += 1;
    publisher.reset();
    setValidation(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [publisher]);

  return {
    client,
    inputRef,
    issues,
    openPicker,
    publisher,
    readyUpload,
    reset,
    stageDroppedFiles,
    stageFiles,
    validation,
  };
};
